import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import DashboardHeader from '../components/DashboardHeader';
import ConstructionGanttChart from '../components/ConstructionGanttChart';
import CustomerProjectForm from '../components/CustomerProjectForm';
import { api } from '../utils/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Calendar, FileText, Bell, AlertCircle, CheckCircle2, Clock, IndianRupee, Plus } from 'lucide-react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

const CustomerDashboard = () => {
  const [user, setUser] = useState(null);
  const [projects, setProjects] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [milestones, setMilestones] = useState([]);
  const [projectMilestones, setProjectMilestones] = useState({});
  const [showNewProject, setShowNewProject] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [userRes, projectsRes, notifRes, unreadRes] = await Promise.all([
        api.get('/auth/me'),
        api.get('/projects'),
        api.get('/notifications'),
        api.get('/notifications/unread-count')
      ]);
      
      setUser(userRes.data);
      setProjects(projectsRes.data);
      setNotifications(notifRes.data.slice(0, 5));
      setUnreadCount(unreadRes.data.count);

      // Fetch milestones for all active projects
      const milestonesMap = {};
      const activeProjects = projectsRes.data.filter(p => ['active', 'completed', 'on_hold'].includes(p.status));
      for (const project of activeProjects) {
        try {
          const milestoneRes = await api.get(`/projects/${project.project_id}/milestones`);
          milestonesMap[project.project_id] = milestoneRes.data;
        } catch (e) {
          milestonesMap[project.project_id] = [];
        }
      }
      setProjectMilestones(milestonesMap);

      // Fetch milestones for the first project if exists (for Gantt chart)
      if (projectsRes.data.length > 0) {
        const milestonesRes = await api.get(`/milestones?project_id=${projectsRes.data[0].project_id}`);
        setMilestones(milestonesRes.data);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getProjectProgress = (project) => {
    // Calculate progress based on approved milestones
    const milestones = projectMilestones[project.project_id] || [];
    if (milestones.length > 0) {
      let totalProgress = 0;
      for (const m of milestones) {
        if (m.status === 'approved') {
          totalProgress += m.weightage || 0;
        }
      }
      return Math.round(totalProgress);
    }
    
    // Fallback for projects without custom milestones
    const statusProgress = {
      'active': 50,
      'completed': 100,
      'on_hold': 30,
      'pending_approval': 10,
      'in_review': 10
    };
    return project.completion_percentage || statusProgress[project.status] || 0;
  };

  const getStatusBadge = (status) => {
    const badges = {
      'active': { label: 'Active', className: 'bg-green-100 text-green-700' },
      'completed': { label: 'Completed', className: 'bg-blue-100 text-blue-700' },
      'on_hold': { label: 'On Hold', className: 'bg-orange-100 text-orange-700' },
      'pending_approval': { label: 'Pending Approval', className: 'bg-amber-100 text-amber-700' },
      'in_review': { label: 'In Review', className: 'bg-purple-100 text-purple-700' },
      'rejected': { label: 'Rejected', className: 'bg-red-100 text-red-700' }
    };
    return badges[status] || { label: status, className: 'bg-stone-100 text-stone-700' };
  };

  // Separate active/approved projects from pending ones
  const activeProjects = projects.filter(p => ['active', 'completed', 'on_hold'].includes(p.status));
  const pendingProjects = projects.filter(p => ['pending_approval', 'in_review'].includes(p.status));

  if (loading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background transition-colors duration-300" data-testid="customer-dashboard">
      <DashboardHeader user={user} unreadCount={unreadCount} />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 pb-24 sm:pb-8">
        {/* Welcome Section - Mobile Optimized */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 sm:mb-8 gap-4"
        >
          <div>
            <h1 className="text-2xl sm:text-3xl font-heading font-bold text-foreground mb-1">
              Welcome back, {user.name?.split(' ')[0]}!
            </h1>
            <p className="text-muted-foreground text-sm sm:text-base">Here&apos;s what&apos;s happening with your projects</p>
          </div>
          <Button 
            onClick={() => setShowNewProject(true)}
            className="w-full sm:w-auto bg-primary hover:bg-primary/90 text-primary-foreground rounded-full h-12 font-semibold shadow-lg shadow-primary/20 active:scale-95 transition-all touch-target"
            data-testid="new-project-button"
          >
            <Plus className="mr-2 h-5 w-5" />
            Request New Project
          </Button>
        </motion.div>

        {/* Quick Stats - Mobile Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6 mb-6 sm:mb-8">
          {[
            { label: 'Active Projects', value: activeProjects.length, icon: Calendar, color: 'text-primary', link: null },
            { label: 'Pending', value: pendingProjects.length, icon: Clock, color: 'text-amber-500', link: null },
            { label: 'Milestones', value: 12, icon: CheckCircle2, color: 'text-green-500', link: activeProjects.length > 0 ? `/projects/${activeProjects[0].project_id}?tab=timeline` : null },
            { label: 'Payments', value: 2, icon: IndianRupee, color: 'text-cyan-500', link: activeProjects.length > 0 ? `/projects/${activeProjects[0].project_id}?tab=payments` : null }
          ].map((stat, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              onClick={() => stat.link && navigate(stat.link)}
              className={stat.link ? 'cursor-pointer' : ''}
              data-testid={`stat-${index}`}
            >
              <Card className={`stat-card border-border bg-card ${stat.link ? 'hover:shadow-lg transition-all duration-300' : ''}`}>
                <CardContent className="p-4 sm:p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs sm:text-sm text-muted-foreground mb-1">{stat.label}</p>
                      <p className="text-2xl sm:text-3xl font-heading font-bold text-foreground">{stat.value}</p>
                    </div>
                    <div className={`p-2 sm:p-3 rounded-xl bg-muted ${stat.color}`}>
                      <stat.icon className="h-5 w-5 sm:h-6 sm:w-6" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
        {/* Projects & Notifications Grid */}
        <div className="grid lg:grid-cols-3 gap-6 sm:gap-8">
          {/* Projects List */}
          <div className="lg:col-span-2 space-y-4 sm:space-y-6">
            {/* Gantt Chart for first project - hidden on mobile */}
            {projects.length > 0 && milestones.length > 0 && (
              <div className="hidden sm:block">
                <ConstructionGanttChart
                  milestones={milestones}
                  projectStartDate={projects[0].start_date}
                  projectEndDate={projects[0].expected_completion_date}
                />
              </div>
            )}

            <div className="flex items-center justify-between">
              <h2 className="text-xl sm:text-2xl font-heading font-bold text-foreground">Your Projects</h2>
            </div>

            {projects.length === 0 ? (
              <Card className="border-border bg-card rounded-2xl shadow-sm">
                <CardContent className="p-8 sm:p-12 text-center">
                  <div className="w-16 h-16 sm:w-20 sm:h-20 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                    <Calendar className="h-8 sm:h-10 w-8 sm:w-10 text-muted-foreground" />
                  </div>
                  <h3 className="text-lg font-heading font-semibold text-foreground mb-2">
                    No projects yet
                  </h3>
                  <p className="text-muted-foreground mb-4 text-sm sm:text-base">
                    Click &quot;Request New Project&quot; to get started with your dream home
                  </p>
                  <Button
                    onClick={() => setShowNewProject(true)}
                    className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-full h-12 font-semibold"
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    Request New Project
                  </Button>
                </CardContent>
              </Card>
            ) : (
              projects.map((project, index) => {
                const statusBadge = getStatusBadge(project.status);
                const isPending = ['pending_approval', 'in_review'].includes(project.status);
                
                return (
                <motion.div
                  key={project.project_id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  {isPending ? (
                    <Card className="border-border bg-card rounded-lg shadow-sm" data-testid={`project-card-${project.project_id}`}>
                      <CardHeader className="pb-3">
                        <div className="flex items-start justify-between">
                          <div>
                            <CardTitle className="text-lg sm:text-xl font-heading text-foreground mb-1">
                              {project.project_name}
                            </CardTitle>
                            <CardDescription className="text-muted-foreground text-sm">
                              {project.location}
                            </CardDescription>
                          </div>
                          <Badge className={`${statusBadge.className} hover:${statusBadge.className} rounded-full text-xs`}>
                            {statusBadge.label}
                          </Badge>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4 text-sm text-amber-800 dark:text-amber-200">
                          <p>Your project request is being reviewed by our team. You&apos;ll be notified once it&apos;s approved with pricing details.</p>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm mt-4">
                          <div>
                            <p className="text-muted-foreground mb-1">Built-up Area</p>
                            <p className="font-medium text-foreground">{project.built_up_area?.toLocaleString('en-IN')} sq.ft</p>
                          </div>
                          <div>
                            <p className="text-muted-foreground mb-1">Submitted On</p>
                            <p className="font-medium text-foreground">
                              {new Date(project.created_at).toLocaleDateString('en-IN')}
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ) : (
                  <Link to={`/projects/${project.project_id}`}>
                    <Card className="border-border bg-card rounded-lg shadow-sm hover:shadow-lg transition-all duration-300 active:scale-[0.98]" data-testid={`project-card-${project.project_id}`}>
                      <CardHeader className="pb-3">
                        <div className="flex items-start justify-between">
                          <div>
                            <CardTitle className="text-lg sm:text-xl font-heading text-foreground mb-1">
                              {project.project_name}
                            </CardTitle>
                            <CardDescription className="text-muted-foreground text-sm">
                              {project.location}
                            </CardDescription>
                          </div>
                          <Badge className={`${statusBadge.className} hover:${statusBadge.className} rounded-full text-xs`}>
                            {statusBadge.label}
                          </Badge>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-4">
                          <div>
                            <div className="flex items-center justify-between text-sm mb-2">
                              <span className="text-muted-foreground">Overall Progress</span>
                              <span className="font-medium text-foreground">{getProjectProgress(project)}%</span>
                            </div>
                            <Progress value={getProjectProgress(project)} className="h-2" />
                          </div>

                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                            <div>
                              <p className="text-muted-foreground mb-1">Contract Value</p>
                              <p className="font-medium text-foreground">
                                {project.contract_value ? `₹${project.contract_value.toLocaleString('en-IN')}` : 'TBD'}
                              </p>
                            </div>
                            <div>
                              <p className="text-muted-foreground mb-1">Expected Completion</p>
                              <p className="font-medium text-foreground">
                                {project.expected_completion_date ? new Date(project.expected_completion_date).toLocaleDateString('en-IN') : 'TBD'}
                              </p>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                  )}
                </motion.div>
              )})
            )}

            {/* Milestone Progress Section */}
            {activeProjects.length > 0 && Object.keys(projectMilestones).length > 0 && (
              <div className="mt-8">
                <h2 className="text-xl sm:text-2xl font-heading font-bold text-foreground mb-4">Milestone Progress</h2>
                {activeProjects.map(project => {
                  const milestones = projectMilestones[project.project_id] || [];
                  if (milestones.length === 0) return null;
                  
                  const approvedCount = milestones.filter(m => m.status === 'approved').length;
                  const totalProgress = milestones.reduce((sum, m) => 
                    m.status === 'approved' ? sum + (m.weightage || 0) : sum, 0
                  );
                  
                  return (
                    <Card key={project.project_id} className="border-border bg-card rounded-lg shadow-sm mb-4">
                      <CardHeader className="pb-2">
                        <div className="flex items-center justify-between">
                          <CardTitle className="text-base font-heading text-foreground">
                            {project.project_name}
                          </CardTitle>
                          <span className="text-sm font-semibold text-primary">{Math.round(totalProgress)}% Complete</span>
                        </div>
                        <Progress value={totalProgress} className="h-2 mt-2" />
                      </CardHeader>
                      <CardContent className="pt-2">
                        <div className="space-y-2">
                          {milestones.sort((a, b) => a.sequence - b.sequence).map(milestone => {
                            const isApproved = milestone.status === 'approved';
                            const photoSrc = milestone.completion_photo
                              ? (milestone.completion_photo.startsWith('/')
                                ? `${process.env.REACT_APP_BACKEND_URL}${milestone.completion_photo}`
                                : milestone.completion_photo)
                              : null;
                            return (
                              <div 
                                key={milestone.milestone_id}
                                className={`flex items-center justify-between p-3 rounded-lg ${
                                  isApproved ? 'bg-green-50 dark:bg-green-950/20' : 'bg-muted/50'
                                }`}
                                data-testid={`customer-milestone-${milestone.milestone_id}`}
                              >
                                <div className="flex items-center gap-3 min-w-0 flex-1">
                                  {isApproved ? (
                                    <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" />
                                  ) : (
                                    <Clock className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                                  )}
                                  <div className="min-w-0 flex-1">
                                    <p className={`font-medium text-sm ${isApproved ? 'text-green-800 dark:text-green-200' : 'text-foreground'}`}>
                                      {milestone.name}
                                    </p>
                                    <p className="text-xs text-muted-foreground">
                                      Due: {new Date(milestone.end_date).toLocaleDateString('en-IN')}
                                    </p>
                                    {isApproved && milestone.completion_remarks && (
                                      <p className="text-xs text-stone-600 dark:text-stone-300 italic mt-1 line-clamp-2">
                                        "{milestone.completion_remarks}"
                                      </p>
                                    )}
                                  </div>
                                </div>
                                <div className="flex items-center gap-3 flex-shrink-0">
                                  {isApproved && photoSrc && (
                                    <a
                                      href={photoSrc}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="w-12 h-12 rounded-md overflow-hidden border border-green-200 hover:scale-105 transition-transform"
                                      data-testid={`customer-milestone-photo-${milestone.milestone_id}`}
                                      title="View completion photo"
                                    >
                                      <img
                                        src={photoSrc}
                                        alt="Completion"
                                        className="w-full h-full object-cover"
                                        onError={(e) => { e.target.parentElement.style.display = 'none'; }}
                                      />
                                    </a>
                                  )}
                                  <div className="text-right">
                                    <p className={`text-sm font-semibold ${isApproved ? 'text-green-600' : 'text-muted-foreground'}`}>
                                      {milestone.weightage}%
                                    </p>
                                    {isApproved && (
                                      <p className="text-xs text-green-600">Completed</p>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        <p className="text-xs text-muted-foreground mt-3">
                          {approvedCount} of {milestones.length} milestones completed
                        </p>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>

          {/* Notifications Sidebar */}
          <div>
            <h2 className="text-xl sm:text-2xl font-heading font-bold text-foreground mb-6">Recent Updates</h2>
            <div className="space-y-4">
              {notifications.length === 0 ? (
                <Card className="border-border bg-card rounded-lg shadow-sm">
                  <CardContent className="p-6 text-center">
                    <Bell className="h-12 w-12 text-muted-foreground/50 mx-auto mb-3" />
                    <p className="text-sm text-muted-foreground">No new notifications</p>
                  </CardContent>
                </Card>
              ) : (
                notifications.map((notif, index) => (
                  <motion.div
                    key={notif.notification_id}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Card className={`border-border bg-card rounded-lg shadow-sm ${!notif.read ? 'ring-2 ring-primary/20 bg-primary/5' : ''}`}>
                      <CardContent className="p-4">
                        <div className="flex items-start space-x-3">
                          <div className="flex-shrink-0">
                            {notif.type === 'progress' && <Clock className="h-5 w-5 text-cyan-500" />}
                            {notif.type === 'document' && <FileText className="h-5 w-5 text-primary" />}
                            {notif.type === 'payment' && <IndianRupee className="h-5 w-5 text-green-500" />}
                            {notif.type === 'material' && <AlertCircle className="h-5 w-5 text-amber-500" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-foreground mb-1">{notif.title}</p>
                            <p className="text-xs text-muted-foreground mb-2">{notif.message}</p>
                            <p className="text-xs text-muted-foreground/70">
                              {new Date(notif.created_at).toLocaleDateString('en-IN', { 
                                day: 'numeric', 
                                month: 'short',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))
              )}
            </div>
          </div>
        </div>
      </main>

      {/* Mobile Bottom Navigation */}
      <div className="fixed bottom-0 left-0 right-0 bg-card border-t border-border lg:hidden z-50">
        <div className="grid grid-cols-4 h-16">
          <button className="flex flex-col items-center justify-center space-y-1 text-primary">
            <Calendar className="h-5 w-5" />
            <span className="text-xs font-medium">Projects</span>
          </button>
          <button 
            onClick={() => setShowNewProject(true)}
            className="flex flex-col items-center justify-center space-y-1 text-muted-foreground active:text-primary transition-colors"
          >
            <Plus className="h-5 w-5" />
            <span className="text-xs font-medium">New</span>
          </button>
          <button className="flex flex-col items-center justify-center space-y-1 text-muted-foreground">
            <Bell className="h-5 w-5" />
            <span className="text-xs font-medium">Updates</span>
            {unreadCount > 0 && (
              <div className="absolute -top-1 -right-1 bg-destructive text-destructive-foreground text-xs rounded-full h-5 w-5 flex items-center justify-center">
                {unreadCount}
              </div>
            )}
          </button>
          <button className="flex flex-col items-center justify-center space-y-1 text-muted-foreground">
            <FileText className="h-5 w-5" />
            <span className="text-xs font-medium">Docs</span>
          </button>
        </div>
      </div>

      {/* New Project Form Modal */}
      <CustomerProjectForm
        open={showNewProject}
        onClose={() => setShowNewProject(false)}
        onSuccess={fetchData}
      />
    </div>
  );
};

export default CustomerDashboard;